import _ from 'lodash';
import { useState, useEffect, useRef, forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Button from '../webapp/core/Button';
import BoothDemo from './BoothDemo';
import PrintDemo from './PrintDemo';
import VideoDemo from './VideoDemo';
import FaceEffectDemo from './FaceEffectDemo';
import ShareDemo from './ShareDemo';
import EmailDemo from './EmailDemo';
import SMSDemo from './SMSDemo';
import LiveGalleryDemo from './LiveGalleryDemo';
import VirtualBoothDemo from './VirtualBoothDemo';
import EventProposalCTA from './EventProposalCTA';
import BackdropDemo from './BackdropDemo';
import ThreeSixtyVideosDemo from '../../fiesta/components/ThreeSixtyVideosDemo.jsx';
import CompanyInfo from '../core/CompanyInfo';
import { isEmail } from '../../utils/inputValidators';
import constants from '../../constants';
import TortillasBeauty from './TortillasBeauty';
import TortillaIntro from './TortillaIntro';
import StickersDemo from './StickersDemo';
import PhotoBackgroundRemovalDemo from './PhotoBackgroundRemovalDemo';
import { getProposalTemplates } from '../../selectors/selectors.js';
import { calculateBackdrop } from '../../utils/proposalBackdrop';
import { getSectionDataAttribute } from '../../utils/proposalSectionIds';

const { staticS3Url } = constants.get();

const EventProposalPage = forwardRef((props, ref) => {
  const {
    id,
    index,
    heading,
    text,
    eventId,
    vw = 0,
    vh = {},
    overlay,
    goToNextPage,
    goToLastPage,
    contrastColor,
    isEmbedMode,
  } = props;

  const event = useSelector((state) => state.proposalData.data);
  const hasPhotoOverlays = useSelector((state) => getProposalTemplates(state).length > 0);
  const previewContainer = useRef(null);
  const contentRef = useRef(null);
  // NOTE: we assume any `eventType` apart from `dslr` should display a Salsa booth.
  // This will change in the future if we need additional conditions for proposals
  // with an `eventType` of `virtual`
  const boothType = event.eventType === 'dslr' ? 'guac' : 'salsa';

  const isVirtual = event.eventType === 'virtual';

  const subdomain = event.liveGallery?.useBrandingSettings
    ? event.brandingSettings?.gallerySubdomain
    : event.liveGallery?.subdomain;

  // Background for capture previews
  // Use shared backdrop calculation utility
  const backdrop = useMemo(() => calculateBackdrop(event, id), [event, id]);

  const startScreenUrl = _.get(event, 'startScreenUrl') || '/static/booth/default-start.jpg';

  // 360 mode overlay is handled by code ported from Fiesta.
  const modeOverlayIds = useMemo(
    () =>
      ['photo', 'gif', 'boomerang', 'video', 'sloMo'].reduce(
        (object, mode) => ({
          ...object,
          [mode]: (event?.captureConfig.modeConfiguration?.[`${mode}Configuration`]?.overlays ?? []).map(
            (overlay) => overlay.overlay[0]
          ),
        }),
        {}
      ),
    [event]
  );

  const greenScreenOverlayIds = useMemo(
    () => (event?.captureConfig.greenScreens ?? []).map((greenScreen) => greenScreen.overlays?.overlay?.[0]),
    [event]
  );

  const previewContent = useRef(
    (() => {
      switch (id) {
        case 'boothPreview':
          if (isVirtual) {
            return (
              <VirtualBoothDemo
                accentColor={event.micrositeAccentColor}
                background={event.virtualBoothConfig.backgroundUrl}
                buttonUrl={event.virtualBoothConfig.buttonUrl}
                title={event.title}
                subtitle={event.virtualBooth?.subtitle}
                logo={event.logoUrl}
                companyInfo={event.liveGallery.companySection ? event.liveGallery.company : null}
                isSimple={true}
              />
            );
          }

          return (
            <BoothDemo
              boothType={boothType}
              ledRing={event.ledPatternModeEnabled ? event.ledBrandingAnimation : false}
              ledColor={!event.ledPatternModeEnabled ? event.ledBrandingColor : false}
              startScreenUrl={startScreenUrl}
            />
          );
        case 'photos':
          if (hasPhotoOverlays) {
            return (
              <PrintDemo
                ref={previewContainer}
                fonts={[...(event.printTemplateFonts || []), ...(event.overlaysFonts || [])]}
                backdrop={backdrop}
                greenScreenOverlayIds={greenScreenOverlayIds}
              />
            );
          }
          return (
            <VideoDemo
              backdrop={backdrop}
              overlay={overlay}
              fiestaOverlayIds={modeOverlayIds.photo}
              greenScreenOverlayIds={greenScreenOverlayIds}
            />
          );
        case 'videos':
          return (
            <VideoDemo
              overlay={overlay}
              video={'/static/capture-modes/video.mp4'}
              greenScreenOverlayIds={greenScreenOverlayIds}
              fiestaOverlayIds={modeOverlayIds.video}
            />
          );
        case 'boomerangs':
          return (
            <VideoDemo
              overlay={overlay}
              video={'/static/capture-modes/boomerang.mp4'}
              fiestaOverlayIds={modeOverlayIds.boomerang}
              greenScreenOverlayIds={greenScreenOverlayIds}
            />
          );
        case 'gifs':
          return (
            <VideoDemo
              backdrop={backdrop}
              overlay={overlay}
              gif={true}
              fiestaOverlayIds={modeOverlayIds.gif}
              greenScreenOverlayIds={greenScreenOverlayIds}
            />
          );
        case 'poseTips':
          return (
            <BoothDemo
              boothType={boothType}
              ledRing={event.ledPatternModeEnabled ? event.ledBrandingAnimation : false}
              ledColor={!event.ledPatternModeEnabled ? event.ledBrandingColor : false}
              poseTips={true}
            />
          );
        case 'faceEffects':
          return <FaceEffectDemo backdrop={backdrop} overlay={overlay} />;
        case 'stickers':
          return <StickersDemo backdrop={backdrop} stickers={event.stickers} />;
        case 'photoBackgroundRemoval':
          return <PhotoBackgroundRemovalDemo backgrounds={event.captureConfig.greenScreens || []} />;
        case 'greenScreens':
          return <VideoDemo backdrop={backdrop} overlay={overlay} greenScreenOverlayIds={greenScreenOverlayIds} />;
        case 'sharing':
          return (
            <ShareDemo
              email={['both', 'email'].includes(event.enabledSharingMethods)}
              text={['both', 'sms'].includes(event.enabledSharingMethods)}
              airDrop={event.airdropEnabled}
              survey={event.surveyEnabled}
              qr={event.qrCodeEnabled}
              print={event.airprintEnabled}
            />
          );
        case 'email':
          return <EmailDemo backdrop={backdrop} template={event.emailTemplate} />;
        case 'sms':
          return <SMSDemo shareText={event.shareText} subdomain={subdomain} />;
        case 'liveGallery':
          return (
            <LiveGalleryDemo
              title={event.title}
              subtitle={event.liveGalleryDemo?.subtitle}
              buttonFlag={event.liveGalleryDemo?.buttonFlag}
              buttonText={event.liveGalleryDemo?.buttonText}
              logo={event.liveGalleryDemo?.logoUrl}
              backdrop={backdrop}
              background={event.liveGalleryDemo?.backgroundUrl}
              accentColor={event.liveGalleryDemo?.micrositeAccentColor}
              theme={event.liveGalleryDemo?.theme}
            />
          );
        case 'virtualBooth':
          return (
            <VirtualBoothDemo
              accentColor={event.micrositeAccentColor}
              background={event.virtualBoothConfig.backgroundUrl}
              buttonUrl={event.virtualBoothConfig.buttonUrl}
              title={event.title}
              subtitle={event.virtualBooth?.subtitle}
              logo={event.logoUrl}
              companyInfo={event.liveGallery.companySection ? event.liveGallery.company : null}
            />
          );
        case 'backdrop':
          return <BackdropDemo backdrop={backdrop} boothType={boothType} />;
        case 'print':
          return <img alt="Chips printer" src="/static/booth/chips.png" />;
        case 'sloMo':
          return (
            <VideoDemo
              overlay={overlay}
              video={`${staticS3Url}/capture-modes/slomo.mp4`}
              fiestaOverlayIds={modeOverlayIds.sloMo}
              greenScreenOverlayIds={greenScreenOverlayIds}
            />
          );
        case 'tortillaIntro':
          return <TortillaIntro />;
        case 'tortillasBeauty':
          return <TortillasBeauty />;
        case 'threeSixtyVideos':
          return (
            <ThreeSixtyVideosDemo
              aspectRatio={event.captureConfig?.modeConfiguration?.threeSixtyConfiguration?.aspectRatio}
            />
          );
        case 'brandedExperience':
          return <EmailDemo backdrop={backdrop} template={event.emailTemplate} />;
        default:
          return null;
      }
    })()
  );

  const renderCTA = () => {
    // For non-end sections, show SEE MORE button to navigate to next section
    if (id !== 'end') {
      return (
        <EventProposalCTA
          accentColor={event.micrositeAccentColor}
          onButtonClick={goToNextPage}
          onBookNowClick={goToLastPage}
        />
      );
    }

    // For end section, show the configured CTA button and company info
    return (
      <>
        {event.proposalConfig.button.enabled && event.proposalConfig.button.text && event.proposalConfig.button.url ? (
          <Button
            color={event.micrositeAccentColor}
            href={
              isEmail.validate(event.proposalConfig.button.url)
                ? `mailto:${event.proposalConfig.button.url}`
                : event.proposalConfig.button.url
            }
            target="_blank"
            isEmbedMode={isEmbedMode}
          >
            {event.proposalConfig.button.text}
          </Button>
        ) : null}
        {!!event.liveGallery.companySection && (
          <CompanyInfo
            company={event.liveGallery.company}
            accentColor={event.micrositeAccentColor}
            contrastColor={contrastColor}
          />
        )}
      </>
    );
  };

  return (
    <section
      ref={ref}
      name={`page-${index}`}
      className={`event-proposal__page event-proposal__page--${id}`}
      data-section={getSectionDataAttribute(id)}
    >
      <div className="wrapper">
        <div className="event-proposal__cols" ref={contentRef}>
          <div className="event-proposal__content">
            <h2 className="event-proposal__heading">{heading}</h2>
            {id === 'end' &&
              event.logoUrl &&
              (event.logoLink ? (
                <a href={event.logoLink}>
                  <img src={event.logoUrl} alt="" className="event-proposal__logo" />
                </a>
              ) : (
                <img src={event.logoUrl} alt="" className="event-proposal__logo" />
              ))}
            <p className="event-proposal__text">{text}</p>
            {renderCTA()}
          </div>
          {previewContent.current && (
            <div className="event-proposal__preview" ref={previewContainer}>
              {previewContent.current}
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

export default EventProposalPage;
